import dot from 'dot-prop-immutable';
import { pathOr } from 'ramda';

import type { ChatChannelType, ChatSupplierMscSettings } from '@frontend/api';

import type { RootState } from 'store/configureStore';

import {
  types,
  type ChatAction,
  type ChatInitialChannelFocusedPayload,
  type CreateAnnotationRequestId,
} from './actions';
import { CHANNEL_TYPES } from './constants';
import type {
  AnnotationToolType,
  ChatDrawerStateByChannelType,
  ChatDrawerStateType,
  ChatId,
  CommentDraft,
  PartMasterVersion,
} from './types';

type AnnotationInputState = {
  /** `null` for the first annotation of a PRIMARY or MSC channel that does not exist yet */
  channelId: ChatId | null;
  channelType: ChatChannelType;
  editorState: CommentDraft;
};

type CommentInputState = {
  annotationId: ChatId;
  editorState: CommentDraft;
};

type AnnotationToolState = {
  channelId: ChatId | null;
  channelType: ChatChannelType | null;
  tool: AnnotationToolType | null;
};

/** How a create annotation request ended. */
export type CreateAnnotationOutcome = 'created' | 'failed';

/** What chat knows of one create annotation request, kept until its sender reads it. */
export type CreateAnnotationRequestRecord = {
  /** Set once the request has created the channel it was sent into. */
  createdChannel?: { channelId: number; channelType: ChatChannelType };
  outcome?: CreateAnnotationOutcome;
};

export type ChatModuleState = {
  activeChannelId: ChatId | null;
  activeChannelType: ChatChannelType | null;
  /** active annotation id keyed by channel id */
  activeThreadByChannels: Record<string, ChatId | null>;
  annotationInputStates: AnnotationInputState[];
  annotationsCreatingForChannelIds: number[];
  annotationsCreatedForChannelIds: number[];
  /** keyed by request id */
  createAnnotationRequests: Record<
    CreateAnnotationRequestId,
    CreateAnnotationRequestRecord
  >;
  annotationTool: AnnotationToolState;
  chatDrawerState: ChatDrawerStateType | null;
  /** From the page that started chat; `null` until it has. */
  drawerStateByChannelType: ChatDrawerStateByChannelType | null;
  /** The channel the latest start of chat opened, for the page that started it. */
  initialChannelFocus: ChatInitialChannelFocusedPayload | null;
  commentInputStates: CommentInputState[];
  commentsCreatedForAnnotationIds: number[];
  commentsCreatingForAnnotationIds: number[];
  /** keyed by comment id */
  editCommentActive: Record<string, boolean>;
  /** keyed by comment id */
  editCommentSaving: Record<string, boolean>;
  isChatDrawerOpen: boolean;
  isCreatingChannelAndAnnotation: boolean;
  isExportControlled: boolean;
  isFetchingSuppliersMscSettings: boolean;
  isInitialStateLoading: boolean;
  isOwner: boolean;
  isReinitializingTrustedThirdPartyChannel: boolean;
  isUpdatingMscConfig: boolean;
  partMasterVersion: PartMasterVersion | null;
  suppliersMscSettings: ChatSupplierMscSettings | null;
  techDataPackageRootUuid: string | null;
};

const initialState: ChatModuleState = {
  activeChannelId: null,
  activeChannelType: null,
  activeThreadByChannels: {},
  annotationInputStates: [],
  annotationsCreatingForChannelIds: [],
  annotationsCreatedForChannelIds: [],
  createAnnotationRequests: {},
  annotationTool: {
    channelId: null,
    channelType: null,
    tool: null,
  },
  chatDrawerState: null,
  drawerStateByChannelType: null,
  initialChannelFocus: null,
  commentInputStates: [],
  commentsCreatedForAnnotationIds: [],
  commentsCreatingForAnnotationIds: [],
  editCommentActive: {},
  editCommentSaving: {},
  isChatDrawerOpen: false,
  isCreatingChannelAndAnnotation: false,
  isExportControlled: false,
  isFetchingSuppliersMscSettings: false,
  isInitialStateLoading: true,
  isOwner: false,
  isReinitializingTrustedThirdPartyChannel: false,
  isUpdatingMscConfig: false,
  partMasterVersion: null,
  suppliersMscSettings: null,
  techDataPackageRootUuid: null,
};

const updateCreateAnnotationRequest = (
  state: ChatModuleState,
  requestId: CreateAnnotationRequestId,
  update: Partial<CreateAnnotationRequestRecord>,
): ChatModuleState['createAnnotationRequests'] => ({
  ...state.createAnnotationRequests,
  [requestId]: { ...state.createAnnotationRequests[requestId], ...update },
});

const reducer = (
  state: ChatModuleState = initialState,
  action: ChatAction,
): ChatModuleState => {
  switch (action.type) {
    case types.CHAT_APP_RESET:
      return {
        ...initialState,
      };
    case types.ANNOTATION_INPUT_RECORD_EDITOR_STATE: {
      const { payload } = action;
      /*
      Remove existing input states for an input then append additional ones.

      The first annotation for a PRIMARY or MSC channel there will be no id,
      so we check by channel id first and if its not there fall back to channel type.
      */
      let nextAnnotationInputStates: AnnotationInputState[];
      if (
        (payload.channelType === CHANNEL_TYPES.MSC && !payload.channelId) ||
        (payload.channelType === CHANNEL_TYPES.PRIMARY && !payload.channelId)
      ) {
        nextAnnotationInputStates = state.annotationInputStates.filter(
          (annotationInputState) =>
            annotationInputState.channelType != payload.channelType,
        );
      } else {
        nextAnnotationInputStates = state.annotationInputStates.filter(
          (annotationInputState) =>
            annotationInputState.channelId != payload.channelId,
        );
      }
      return {
        ...state,
        annotationInputStates: nextAnnotationInputStates.concat([
          {
            channelId: payload.channelId,
            channelType: payload.channelType,
            editorState: payload.editorState,
          },
        ]),
      };
    }
    case types.ANNOTATION_TOOL_SET_ACTIVE: {
      const { payload } = action;
      return {
        ...state,
        annotationTool: {
          channelId: payload.channelId,
          channelType: payload.channelType,
          tool: payload.tool,
        },
      };
    }
    case types.ANNOTATION_TOOL_SET_INACTIVE:
      return {
        ...state,
        annotationTool: {
          channelId: null,
          channelType: null,
          tool: null,
        },
      };
    case types.CHAT_DRAWER_CLOSE:
      return {
        ...state,
        isChatDrawerOpen: false,
      };
    case types.CHAT_DRAWER_OPEN:
      return {
        ...state,
        isChatDrawerOpen: true,
      };
    case types.CHAT_DRAWER_STATE_SET: {
      const { payload } = action;
      return {
        ...state,
        activeChannelId: payload.activeChannelId,
        activeChannelType: payload.activeChannelType,
        chatDrawerState: payload.chatDrawerState,
        isInitialStateLoading: false,
      };
    }
    // Keeps what chat already holds, such as unsent drafts: a page that wants
    // chat fresh resets it first.
    case types.CHAT_APP_INIT: {
      const { payload } = action;
      return {
        ...state,
        drawerStateByChannelType: payload.drawerStateByChannelType,
        initialChannelFocus: null,
        isInitialStateLoading: true,
        isOwner: payload.isOwner,
        isExportControlled: payload.isExportControlled,
        partMasterVersion: payload.partMasterVersion,
        techDataPackageRootUuid: payload.techDataPackageRootUuid,
      };
    }
    case types.CHAT_APP_INITIAL_CHANNEL_FOCUSED:
      return { ...state, initialChannelFocus: action.payload };
    case types.COMMENT_INPUT_RECORD_EDITOR_STATE: {
      const { payload } = action;
      const nextCommentInputStates = state.commentInputStates.filter(
        (commeintInputState) =>
          commeintInputState.annotationId != payload.annotationId,
      );

      return {
        ...state,
        commentInputStates: nextCommentInputStates.concat([
          {
            annotationId: payload.annotationId,
            editorState: payload.editorState,
          },
        ]),
      };
    }
    case types.CREATE_ANNOTATION_ERROR: {
      const { payload } = action;
      // for annotations being created for new channels (ie. channelId = null) we will clear all annotationsCreating records
      let nextAnnotationsCreatingForChannelIds: number[] = [];
      if (payload.channelId) {
        nextAnnotationsCreatingForChannelIds =
          state.annotationsCreatingForChannelIds.filter(
            (id) => payload.channelId != id,
          );
      }
      return {
        ...state,
        annotationsCreatingForChannelIds: nextAnnotationsCreatingForChannelIds,
        createAnnotationRequests: updateCreateAnnotationRequest(
          state,
          payload.requestId,
          { outcome: 'failed' },
        ),
        isCreatingChannelAndAnnotation: false,
      };
    }
    case types.CREATE_ANNOTATION_OUTCOME_READ: {
      const { [action.payload.requestId]: _read, ...createAnnotationRequests } =
        state.createAnnotationRequests;
      return { ...state, createAnnotationRequests };
    }
    case types.CREATE_ANNOTATION_INPUT_REINITIALIZED: {
      const { payload } = action;
      return {
        ...state,
        annotationsCreatedForChannelIds:
          state.annotationsCreatedForChannelIds.filter(
            (id) => id != payload.channelId,
          ),
      };
    }
    case types.CREATE_ANNOTATION_SUCCESS: {
      const { channelId, channelType, requestId } = action.payload;

      const nextAnnotationsCreatedForChannelIds = channelId
        ? state.annotationsCreatedForChannelIds.concat([Number(channelId)])
        : state.annotationsCreatedForChannelIds.concat([]);

      const nextAnnotationInputStates = state.annotationInputStates.filter(
        (inputState) => {
          if (channelType == CHANNEL_TYPES.SECONDARY) {
            return inputState.channelId != channelId;
          } else {
            return inputState.channelType != channelType;
          }
        },
      );

      return {
        ...state,
        annotationInputStates: nextAnnotationInputStates,
        annotationsCreatingForChannelIds:
          state.annotationsCreatingForChannelIds.filter(
            (id) => id != channelId,
          ),
        annotationsCreatedForChannelIds: nextAnnotationsCreatedForChannelIds,
        createAnnotationRequests: updateCreateAnnotationRequest(
          state,
          requestId,
          { outcome: 'created' },
        ),
        isCreatingChannelAndAnnotation: false,
      };
    }
    case types.CREATE_ANNOTATION_RECORD_ANNOATION_CREATING: {
      // The request created its channel, and now creates the annotation in it.
      const { channelId, channelType, requestId } = action.payload;
      return {
        ...state,
        annotationsCreatingForChannelIds:
          state.annotationsCreatingForChannelIds.concat([channelId]),
        createAnnotationRequests: updateCreateAnnotationRequest(
          state,
          requestId,
          { createdChannel: { channelId, channelType } },
        ),
        isCreatingChannelAndAnnotation: false,
      };
    }
    case types.CREATE_ANNOTATION_REQUEST: {
      const { channelId } = action.payload;
      const isCreatingChannelAndAnnotation = channelId ? false : true;
      const annotationsCreatingForChannelIds = channelId
        ? state.annotationsCreatingForChannelIds.concat([Number(channelId)])
        : state.annotationsCreatingForChannelIds.concat([]);
      return {
        ...state,
        annotationsCreatingForChannelIds,
        isCreatingChannelAndAnnotation,
      };
    }
    case types.CREATE_COMMENT_ERROR: {
      const { payload } = action;
      return {
        ...state,
        commentsCreatingForAnnotationIds:
          state.commentsCreatingForAnnotationIds.filter(
            (annotationId) => annotationId != payload.annotationId,
          ),
      };
    }
    case types.CREATE_COMMENT_INPUT_REINITIALIZED: {
      const { payload } = action;
      return {
        ...state,
        commentsCreatedForAnnotationIds:
          state.commentsCreatedForAnnotationIds.filter(
            (id) => id != payload.annotationId,
          ),
      };
    }
    case types.CREATE_COMMENT_REQUEST: {
      return {
        ...state,
        commentsCreatingForAnnotationIds:
          state.commentsCreatingForAnnotationIds.concat([
            Number(action.payload.annotationId),
          ]),
      };
    }
    case types.CREATE_COMMENT_SUCCESS: {
      const { annotationId } = action.payload;

      const nextCommentsCreatedForAnnotationIds = annotationId
        ? state.commentsCreatedForAnnotationIds.concat([Number(annotationId)])
        : state.commentsCreatedForAnnotationIds.concat([]);

      const nextCommentInputStates = state.commentInputStates.filter(
        (commentInputState) => commentInputState.annotationId != annotationId,
      );

      return {
        ...state,
        commentInputStates: nextCommentInputStates,
        commentsCreatingForAnnotationIds:
          state.commentsCreatingForAnnotationIds.filter(
            (id) => annotationId != id,
          ),
        commentsCreatedForAnnotationIds: nextCommentsCreatedForAnnotationIds,
      };
    }
    case types.EDIT_COMMENT_CANCEL_BY_ID:
      return dot.merge(state, 'editCommentActive', {
        [action.payload.commentId]: false,
      });
    case types.EDIT_COMMENT_ERROR:
      return dot.merge(state, 'editCommentSaving', {
        [String(action.payload.commentId)]: false,
      });
    case types.EDIT_COMMENT_REQUEST:
      return dot.merge(state, 'editCommentSaving', {
        [action.payload.commentId]: true,
      });
    case types.EDIT_COMMENT_SET_BY_ID:
      return dot.merge(state, 'editCommentActive', {
        [action.payload.commentId]: true,
      });
    case types.EDIT_COMMENT_SUCCESS: {
      const { payload } = action;
      return {
        ...state,
        editCommentActive: dot.set(
          state.editCommentActive,
          payload.commentId,
          false,
        ),
        editCommentSaving: dot.set(
          state.editCommentSaving,
          payload.commentId,
          false,
        ),
      };
    }
    case types.EDIT_MSC_CHANNEL_CONFIG_ERROR:
    case types.EDIT_MSC_CHANNEL_CONFIG_SUCCESS:
      return {
        ...state,
        isUpdatingMscConfig: false,
      };
    case types.EDIT_MSC_CHANNEL_CONFIG_REQUEST:
      return {
        ...state,
        isUpdatingMscConfig: true,
      };
    case types.FETCH_SUPPLIERS_MSC_SETTINGS_ERROR:
      return {
        ...state,
        isFetchingSuppliersMscSettings: false,
      };
    case types.FETCH_SUPPLIERS_MSC_SETTINGS_REQUEST:
      return {
        ...state,
        isFetchingSuppliersMscSettings: true,
      };
    case types.FETCH_SUPPLIERS_MSC_SETTINGS_SUCCESS:
      return {
        ...state,
        isFetchingSuppliersMscSettings: false,
        suppliersMscSettings: action.payload.suppliersMscSettings,
      };
    case types.THREAD_SET_ACTIVE: {
      const { payload } = action;
      return {
        ...state,
        activeThreadByChannels: dot.set(
          state.activeThreadByChannels,
          payload.channelId,
          payload.annotationId,
        ),
      };
    }
    case types.REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_ERROR:
    case types.REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_SUCCESS:
      return {
        ...state,
        isReinitializingTrustedThirdPartyChannel: false,
      };
    case types.REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_REQUEST:
      return {
        ...state,
        isReinitializingTrustedThirdPartyChannel: true,
      };
    case types.THREAD_SET_INACTIVE:
      return {
        ...state,
        activeThreadByChannels: dot.set(
          state.activeThreadByChannels,
          action.payload.channelId,
          null,
        ),
      };
    default:
      return state;
  }
};

const getScope = (state: RootState): ChatModuleState =>
  state.modules.common.chat.chat;

export const getActiveChannelId = (state: RootState) =>
  getScope(state).activeChannelId;

export const getActiveChannelType = (state: RootState) =>
  getScope(state).activeChannelType;

export const getAnnotationToolState = (state: RootState) =>
  getScope(state).annotationTool;

export const getActiveThreadByChannel = (
  state: RootState,
  channelId: ChatId | null = null,
): ChatId | null =>
  pathOr(null, [String(channelId)], getScope(state).activeThreadByChannels);

export const getActiveThread = (state: RootState) => {
  const activeChannelId = getActiveChannelId(state);
  return getActiveThreadByChannel(state, activeChannelId);
};

export const getAnnotationInputStateForChannel = (
  state: RootState,
  channelId: ChatId | null | undefined,
  channelType: ChatChannelType | null | undefined,
) => {
  if (channelId) {
    return getScope(state).annotationInputStates.find(
      (inputState) => inputState.channelId == channelId,
    );
  } else {
    return getScope(state).annotationInputStates.find(
      (inputState) => inputState.channelType == channelType,
    );
  }
};

export const getChatDrawerState = (state: RootState) =>
  getScope(state).chatDrawerState;

export const getCommentInputStateForAnnotation = (
  state: RootState,
  annotationId: ChatId | null | undefined,
) =>
  getScope(state).commentInputStates.find(
    (inputState) => inputState.annotationId == annotationId,
  );

export const isCommentCreatingForAnnotation = (
  state: RootState,
  annotationId: ChatId | null | undefined,
) =>
  getScope(state).commentsCreatingForAnnotationIds.includes(
    Number(annotationId),
  );

export const getCommentsCreatingForAnnotationIds = (state: RootState) =>
  getScope(state).commentsCreatingForAnnotationIds;

export const getEditCommentSaving = (state: RootState) =>
  getScope(state).editCommentSaving;

export const isCommentBeingEdited = (
  state: RootState,
  commentId: ChatId,
): boolean => pathOr(false, ['editCommentActive', commentId], getScope(state));

export const isCommentSaving = (state: RootState, commentId: ChatId): boolean =>
  pathOr(false, ['editCommentSaving', commentId], getScope(state));

export const hasCreatedCommentForAnnotation = (
  state: RootState,
  annotationId: ChatId | null | undefined,
) =>
  getScope(state).commentsCreatedForAnnotationIds.includes(
    Number(annotationId),
  );

/** Every create annotation request whose sender has not read it yet, by id. */
export const getCreateAnnotationRequests = (state: RootState) =>
  getScope(state).createAnnotationRequests;

export const getCreateAnnotationRequest = (
  state: RootState,
  requestId: CreateAnnotationRequestId,
): CreateAnnotationRequestRecord | undefined =>
  getScope(state).createAnnotationRequests[requestId];

export const getDrawerStateByChannelType = (state: RootState) =>
  getScope(state).drawerStateByChannelType;

export const getInitialChannelFocus = (state: RootState) =>
  getScope(state).initialChannelFocus;

export const getAnnotationsCreatingForChannelIds = (state: RootState) =>
  getScope(state).annotationsCreatingForChannelIds;

export const getIsCreatingChannelAndAnnotation = (state: RootState) =>
  getScope(state).isCreatingChannelAndAnnotation;

export const isCreatingAnnotationForChannel = (
  state: RootState,
  channelId: ChatId | null = null,
) => {
  if (channelId) {
    return getScope(state).annotationsCreatingForChannelIds.includes(
      Number(channelId),
    );
  } else {
    return getScope(state).isCreatingChannelAndAnnotation;
  }
};

export const isReinitializingTrustedThirdPartyChannel = (state: RootState) =>
  getScope(state).isReinitializingTrustedThirdPartyChannel;

export const getIsOwner = (state: RootState) => getScope(state).isOwner;

export const getIsFetchingSuppliersMscSettings = (state: RootState) =>
  getScope(state).isFetchingSuppliersMscSettings;

export const getIsUpdatingMscConfig = (state: RootState) =>
  getScope(state).isUpdatingMscConfig;

export const hasCreatedAnnotationForChannel = (
  state: RootState,
  channelId: ChatId | null = null,
) => {
  if (channelId) {
    return getScope(state).annotationsCreatedForChannelIds.includes(
      Number(channelId),
    );
  } else {
    // TODO - handle this case
    return getScope(state).isCreatingChannelAndAnnotation;
  }
};

export const isChatDrawerOpen = (state: RootState) =>
  getScope(state).isChatDrawerOpen;

export const isCreatingAnnotation = (state: RootState) =>
  getScope(state).annotationsCreatingForChannelIds.length > 0;

export const isCreatingComment = (state: RootState) =>
  getScope(state).commentsCreatingForAnnotationIds.length > 0;

export const getIsInitialStateLoading = (state: RootState) =>
  getScope(state).isInitialStateLoading;

export const getSuppliersMscSettings = (state: RootState) =>
  getScope(state).suppliersMscSettings;

export const getTechDataPackageRootUuid = (state: RootState) =>
  getScope(state).techDataPackageRootUuid;

export const getPartMasterVersion = (state: RootState) =>
  getScope(state).partMasterVersion;

export const getIsExportControlled = (state: RootState) =>
  getScope(state).isExportControlled;

export default reducer;
